import {useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {MainBoxClient} from '../CreateClient/CreateClientStyles'
import {StyledTypography} from '../AdminPanelStyling'
import {CancelButton, SaveButton} from '../ProjectDetails/ProjectDetailStyle'
import IndividualProfileComponent from '../ProjectDetails/IndividualProfile/IndividualProfileComponent'
import IndividualHealthFilterComponent from '../ProjectDetails/IndividualHealthFilter/IndividualHealthFilterComponent'
import FinancialWellbeingComponent from '../ProjectDetails/FinancialWellbeing/FinancialWellbeingComponent'
import HomeAndHouseholdFilterComponent from '../ProjectDetails/HomeAndHouseholdFilters/HomeAndHouseholdFilterComponent'
import ResourceAccessBarriersComponent from '../ProjectDetails/ResourceAccessBarriers/ResourceAccessBarriersComponent'
import CommunitySupportComponent from '../ProjectDetails/CommunitySupport/CommunitySupportComponent'
import EnvironmentalExposureComponent from '../ProjectDetails/EnvironmentalExposure/EnvironmentalExposureComponent'
import SectionDividerLineComponent from '../common/SectionDividerLine/SectionDividerLineComponent'
import JurisdictionComponent from '../ProjectDetails/Jurisdiction/JurisdictionComponent'
import DataAggregation from '../ProjectDetails/DataAggregation/DataAggregation'
import PointOfInterest from '../ProjectDetails/PointOfInterest/PointOfInterest'
import TrendAnalysisComponent from '../ProjectDetails/TrendAnalysis/TrendAnalysis'
import ExportPermissionComponent from '../ProjectDetails/ExportPermissionComponent/ExportPermissionComponent'
import ChatbotComponent from '../ProjectDetails/chatbot/ToggleChatbot'
import ChatbotExport from '../ProjectDetails/chatbot/ChatbotExport'
import ChatbotCopy from '../ProjectDetails/chatbot/ChatbotCopy'
import Workforces from '../ProjectDetails/Workforces'
import Benefits from '../ProjectDetails/Benefits'
import {useAdminPermission} from '../../store/hooks/adminPermission'
import {useUpdateCustomSettings} from '../../store/hooks/customSettings'
import {
  selectedJurisdictionChanged,
  selectedDataAggregationChanged,
  selectedTrendAnalysisChanged,
  selectedUserFeedbackChanged,
  selectedChatbotChanged,
  selectedExportToggleChanged,
  selectedBenefitsChanged,
  selectedWorkforcesChanged,
  selectedPOIChanged,
  selectedPersonaChanged,
  selectedUseCasesChanged,
  selectedChatbotExportChanged,
  selectedChatbotCopyChanged
} from '../ProjectDetails/Helper/CompareChanges'
import CancelPopup from './CancelPopup'
import LoadingIcon from '../common/LoadingIcon/LoadingIcon'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import PersonaFlag from '../ProjectDetails/PersonaFlag/PersonaFlag'
import UseCases from '../ProjectDetails/UseCases'
import CustomizationDialog from './Customization/CustomizationDialog'
import {
  StyledFlexBox,
  StyledNotificationInfoOutlinedIcon,
  StyledNotificationTextBox
} from '../ProjectDetails/UseCases/UseCaseStyles'
import {
  getSelectedUseCases,
  handleUpdateSelectedUseCases,
  updateSelectedData
} from '../ProjectDetails/Helper/UseCaseHelper'
import ConfirmationDialog from '../ProjectDetails/UseCases/ConfirmationDialog'
import UseCaseNotificationAlert from '../ProjectDetails/UseCases/UseCaseNotificationAlert'
import {getFormattedCustomLabelsRequest} from './Customization/helper'
import {useConfig} from '../../store/hooks/config'
import {useSelector} from 'react-redux'
import {selectFilterData} from '../../store/slices/adminPermissionSlice'
import UserFeedbackComponent from '../ProjectDetails/UserFeedback/UserFeedback'

const title = 'Manage My Permissions'
const CustomProjectDetails = ({setIsAdminPanelOpen, resetView = () => {}}) => {
  const filterData = useSelector(selectFilterData)
  const {
    jurisdiction,
    dataAggregationConfig,
    filters,
    benefits,
    workforces,
    useCaseConfigData,
    pointOfInterestsData,
    currentBenefits,
    currentJurisdiction,
    currentWorkforces,
    currentUseCases,
    currentPOI,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentPersonaFlag,
    currentDataAggregation,
    loadingUpdatedPermission,
    errorUpdatedPermission,
    filterDetailsError,
    loadingHealthAndEquityConditions,
    errorLoadingHealthAndEquityConditions,
    loadingStates,
    errorLoadingStates,
    reloadFilters,
    updatedLabelLoading,
    customizationData,
    updateCustomizationLabels,
    updatedLabelError,
    setRefreshDashboard,
    updateCustomizationWithDefaultData
  } = useAdminPermission()

  useEffect(() => {
    setIsAdminPanelOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selectedJurisdiction, setSelectedJurisdiction] = useState([])
  const [selectedDataAggregation, setSelectedDataAggregation] = useState([])
  const [selectedFilters, setSelectedFilters] = useState([])
  const [selectedPOI, setSelectedPOI] = useState([])
  const [selectedBenefits, setSelectedBenefits] = useState([])
  const [selectedWorkforces, setSelectedWorkforces] = useState([])
  const [selectedTrendAnalysis, setSelectedTrendAnalysis] = useState([0])
  const [selectedChatbot, setSelectedChatbot] = useState([0])
  const [selectedChatbotExport, setSelectedChatbotExport] = useState([0])
  const [selectedChatbotCopy, setSelectedChatbotCopy] = useState([0])
  const [selectedExportToggle, setSelectedExportToggle] = useState([0])
  const [selectedPersona, setSelectedPersona] = useState([0])
  const [selectedUserFeedback, setSelectedUserFeedback] = useState([0])
  const {handleUpdate} = useUpdateCustomSettings()
  const [navigateToAdminPanel, setNavigateToAdminPanel] = useState(false)
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true)
  const [open, setOpen] = useState(false)
  const [customLabelDialogProps, setCustomLabelDialogProps] = useState({open: false, section: '', data: []})
  const [selectedUseCases, setSelectedUseCases] = useState([])
  const [updatedUseCases, setUpdatedUseCases] = useState([])
  const [affectedUseCases, setAffectedUseCases] = useState([])
  const [filterToBeDeleted, setFilterToBeDeleted] = useState({})
  const [showUseCaseConfirmationDialog, setShowUseCaseConfirmationDialog] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [showInfoAlert, setShowInfoAlert] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const {personasConfig, filterConfig} = useConfig()
  const [isLabelCustomized, setIsLabelCustomized] = useState(false)
  const [closeExistingEventFlag, setCloseExistingEventFlag] = useState(false)

  const hasAnyFilters = useMemo(() => selectedFilters.length > 0, [selectedFilters])

  // Helper function to extract filters array from filterData
  const getFiltersArray = (filterData) => {
    if (Array.isArray(filterData)) {
      return filterData
    } else if (filterData?.adminPermission?.filters) {
      return filterData.adminPermission.filters
    } else if (filterData?.filters) {
      return filterData.filters
    }
    return []
  }

  useEffect(() => {
    if (showNotification && !loadingUpdatedPermission && !updatedLabelLoading) {
      reloadFilters()
      setRefreshDashboard(true)
      setIsSaving(false)
      if (!errorUpdatedPermission && !updatedLabelError) {
        setIsLabelCustomized(false)
      }
    }
  }, [
    showNotification,
    loadingUpdatedPermission,
    updatedLabelLoading,
    errorUpdatedPermission,
    updatedLabelError,
    reloadFilters,
    setRefreshDashboard
  ])

  useEffect(() => {
    setSelectedUseCases([...getSelectedUseCases(updatedUseCases)])
  }, [updatedUseCases])

  const handleOptionDeletion = (type, data, option) => {
    setFilterToBeDeleted({type, data, option})
    const selectedUseCases = []
    for (const item of updatedUseCases || []) {
      for (const topic of item.topics) {
        handleUpdateSelectedUseCases(topic, selectedUseCases, option)
      }
    }
    setAffectedUseCases([...selectedUseCases])
    if (selectedUseCases.length > 0) {
      setShowUseCaseConfirmationDialog(true)
    } else {
      updateSelectedData(type, data, option, setSelectedFilters)
    }
  }

  const handleConfirmOptionDeletion = () => {
    const {type, data, option} = filterToBeDeleted || {}
    updateSelectedData(type, data, option, setSelectedFilters)
    setShowUseCaseConfirmationDialog(false)
    setShowInfoAlert(true)
  }

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleCancel = () => {
    if (saveButtonDisableConditions()) {
      setNavigateToAdminPanel(true)
    } else {
      setOpen(true)
    }
  }

  useEffect(() => {
    if (!loadingUpdatedPermission && navigateToAdminPanel) {
      // Reset view to clients tab since we are already on the admin panel
      resetView()
    }
  }, [loadingUpdatedPermission, navigateToAdminPanel, resetView])

  // Initialize selected values based on admin permissions
  useEffect(() => {
    // Extract filters array from filterData
    const filtersArray = getFiltersArray(filterData)

    // Use IS_PERMISSIONED to get currently saved/enabled filters
    // Handle both boolean true and string "true"
    const permissionedFilters = filtersArray.filter(
      (option) => option.IS_PERMISSIONED === true || option.IS_PERMISSIONED === 'true'
    )

    setSelectedFilters(permissionedFilters)

    setSelectedJurisdiction(jurisdiction.filter((option) => currentJurisdiction.includes(option.STATE_ID)))
    setSelectedDataAggregation(
      dataAggregationConfig?.data?.filter((option) => currentDataAggregation.includes(option.key)) || []
    )
    setSelectedPOI(pointOfInterestsData.filter((option) => currentPOI.includes(Number.parseInt(option.POI_ID))) || [])
    setSelectedBenefits(
      benefits.filter(
        (option) => currentBenefits && currentBenefits.length > 0 && currentBenefits.includes(option.id)
      ) || []
    )
    setSelectedWorkforces(
      workforces.filter(
        (option) => currentWorkforces && currentWorkforces.length > 0 && currentWorkforces.includes(option.id)
      ) || []
    )
    setSelectedUseCases(currentUseCases || [])
    setSelectedTrendAnalysis(currentTrendAnalysisFlag)
    setSelectedUserFeedback(currentUserFeedbackFlag)
    setSelectedChatbot(currentChatbotFlag)
    setSelectedChatbotExport(currentChatbotExportFlag)
    setSelectedChatbotCopy(currentChatbotCopyFlag)
    setSelectedExportToggle(currentExportFlag)
    setSelectedPersona(currentPersonaFlag)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterData,
    currentJurisdiction,
    currentBenefits,
    currentWorkforces,
    currentUseCases,
    currentDataAggregation,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentPersonaFlag,
    currentPOI
  ])

  useEffect(() => {
    // Get currently selected filter keys (sorted for comparison)
    const selectedFilterKeys = selectedFilters
      .map((f) => f.FILTER_KEY)
      .sort((a, b) => a.localeCompare(b))
      .toString()

    // Get originally saved/permissioned filter keys (using IS_PERMISSIONED from filterData)
    // Handle both boolean true and string "true"
    const filtersArray = getFiltersArray(filterData)

    const permissionedFilterKeys = filtersArray
      .filter((f) => f.IS_PERMISSIONED === true || f.IS_PERMISSIONED === 'true')
      .map((f) => f.FILTER_KEY)
      .sort((a, b) => a.localeCompare(b))
      .toString()

    if (
      selectedFilters.length === 0 ||
      selectedJurisdiction.length === 0 ||
      selectedDataAggregation.length === 0 ||
      selectedPOI.length === 0
    ) {
      setSaveButtonDisabled(true)
    } else if (
      selectedFilterKeys !== permissionedFilterKeys ||
      selectedBenefitsChanged(selectedBenefits, currentBenefits) ||
      selectedWorkforcesChanged(selectedWorkforces, currentWorkforces) ||
      selectedJurisdictionChanged(selectedJurisdiction, currentJurisdiction) ||
      selectedDataAggregationChanged(selectedDataAggregation, currentDataAggregation) ||
      selectedUseCasesChanged(selectedUseCases, currentUseCases) ||
      selectedTrendAnalysisChanged(selectedTrendAnalysis, currentTrendAnalysisFlag) ||
      selectedUserFeedbackChanged(selectedUserFeedback, currentUserFeedbackFlag) ||
      selectedChatbotChanged(selectedChatbot, currentChatbotFlag) ||
      selectedChatbotExportChanged(selectedChatbotExport, currentChatbotExportFlag) ||
      selectedChatbotCopyChanged(selectedChatbotCopy, currentChatbotCopyFlag) ||
      selectedExportToggleChanged(selectedExportToggle, currentExportFlag) ||
      selectedPOIChanged(selectedPOI, currentPOI) ||
      selectedPersonaChanged(selectedPersona, currentPersonaFlag) ||
      isLabelCustomized
    ) {
      setSaveButtonDisabled(false)
    } else {
      setSaveButtonDisabled(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFilters,
    selectedJurisdiction,
    selectedBenefits,
    selectedWorkforces,
    selectedUseCases,
    selectedDataAggregation,
    selectedTrendAnalysis,
    selectedUserFeedback,
    selectedChatbot,
    selectedChatbotExport,
    selectedChatbotCopy,
    selectedExportToggle,
    selectedPersona,
    selectedPOI,
    currentBenefits,
    currentWorkforces,
    currentJurisdiction,
    currentDataAggregation,
    currentUseCases,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentPersonaFlag,
    currentPOI,
    filters,
    isLabelCustomized
  ])

  const handleSave = (exit) => {
    const formatted = getFormattedCustomLabelsRequest(customizationData, personasConfig, filterConfig)
    setIsSaving(true)
    if (isLabelCustomized) {
      updateCustomizationLabels(formatted)
    }

    const updatePayload = {
      jurisdiction: selectedJurisdiction.map((element) => element.STATE_ID),
      dataAggregation: selectedDataAggregation.map((element) => element.key),
      filters: selectedFilters.map((f) => f.FILTER_KEY),
      usecase: selectedUseCases,
      benefits: selectedBenefits.map((element) => element.id),
      workforces: selectedWorkforces.map((element) => element.id),
      pointOfInterest: selectedPOI.map((element) => element.POI_ID),
      trendAnalysisFlag: selectedTrendAnalysis,
      userFeedbackFlag: selectedUserFeedback,
      chatbotFlag: selectedChatbot,
      chatbotExportFlag: selectedChatbotExport,
      chatbotCopyFlag: selectedChatbotCopy,
      exportFlag: selectedExportToggle,
      personaFlag: selectedPersona,
      closeExistingEventFlag
    }

    const updateDetails = {
      selectedJurisdiction,
      selectedFilters,
      exit,
      selectedTrendAnalysis,
      selectedUserFeedback,
      selectedExportToggle,
      selectedChatbot,
      selectedChatbotExport,
      selectedChatbotCopy,
      selectedPersona,
      updatePayload,
      setShowNotification,
      closeExistingEventFlag
    }

    handleUpdate(updateDetails, handleClickOpen, setShowNotification, setNavigateToAdminPanel)
  }
  const saveButtonDisableConditions = () => {
    const result =
      showNotification ||
      saveButtonDisabled ||
      loadingUpdatedPermission ||
      errorUpdatedPermission ||
      filterDetailsError ||
      loadingHealthAndEquityConditions ||
      errorLoadingHealthAndEquityConditions ||
      loadingStates ||
      errorLoadingStates ||
      selectedPOI.length === 0
    return result
  }

  if (isSaving || loadingUpdatedPermission || updatedLabelLoading || !filterData) {
    return (
      <MainBoxClient component='main' alignContent={'center'}>
        <LoadingIcon title='Manage My Permissions' />
      </MainBoxClient>
    )
  }
  return (
    <MainBoxClient component='main'>
      <StyledTypography variant='h5' tabIndex={0} title={title} aria-label={title}>
        {title}
      </StyledTypography>
      <UserFeedbackComponent
        setCloseExistingEventFlag={setCloseExistingEventFlag}
        selectedFeedback={selectedUserFeedback}
        setSelectedFeedback={setSelectedUserFeedback}
      />
      <SectionDividerLineComponent />
      <div>
        <JurisdictionComponent
          selectedData={selectedJurisdiction}
          setJurisdictionValue={setSelectedJurisdiction}
          enabled={true}
          isCompleted={true}
        />
      </div>
      <SectionDividerLineComponent />
      <div>
        <DataAggregation
          selectedData={selectedDataAggregation}
          setSelectedDataAggregation={setSelectedDataAggregation}
          enabled={true}
          isCompleted={true}
        />
      </div>
      <SectionDividerLineComponent />
      <div>
        <IndividualProfileComponent
          selectedData={selectedFilters}
          setFilterValue={setSelectedFilters}
          enabled={true}
          isCompleted={true}
          handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
          showCustomLabels={true}
          setCustomLabelDialogProps={setCustomLabelDialogProps}
          hasAnyFilters={hasAnyFilters}
        />
      </div>
      <SectionDividerLineComponent />
      <IndividualHealthFilterComponent
        selectedData={selectedFilters}
        setFilterValue={setSelectedFilters}
        enabled={true}
        isCompleted={true}
        handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
        hasAnyFilters={hasAnyFilters}
      />
      <SectionDividerLineComponent />
      <FinancialWellbeingComponent
        selectedData={selectedFilters}
        setFilterValue={setSelectedFilters}
        enabled={true}
        isCompleted={true}
        handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
        hasAnyFilters={hasAnyFilters}
      />
      <SectionDividerLineComponent />
      <HomeAndHouseholdFilterComponent
        selectedData={selectedFilters}
        setFilterValue={setSelectedFilters}
        enabled={true}
        isCompleted={true}
        handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
        hasAnyFilters={hasAnyFilters}
      />
      <SectionDividerLineComponent />
      <ResourceAccessBarriersComponent
        selectedData={selectedFilters}
        setFilterValue={setSelectedFilters}
        enabled={true}
        isCompleted={true}
        handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
        hasAnyFilters={hasAnyFilters}
      />
      <SectionDividerLineComponent />
      <CommunitySupportComponent
        selectedData={selectedFilters}
        setFilterValue={setSelectedFilters}
        enabled={true}
        isCompleted={true}
        handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
        hasAnyFilters={hasAnyFilters}
      />
      <SectionDividerLineComponent />
      <EnvironmentalExposureComponent
        selectedData={selectedFilters}
        setFilterValue={setSelectedFilters}
        enabled={true}
        isCompleted={true}
        handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
        hasAnyFilters={hasAnyFilters}
      />
      <SectionDividerLineComponent />
      <UseCases
        useCaseConfigData={useCaseConfigData}
        selectedFilters={selectedFilters}
        setUpdatedUseCases={setUpdatedUseCases}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
      />
      <SectionDividerLineComponent />
      <PersonaFlag
        selectedPersona={selectedPersona}
        setselectedPersona={setSelectedPersona}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
      />
      <SectionDividerLineComponent />
      <PointOfInterest
        selectedData={selectedPOI}
        setPointofInterestValue={setSelectedPOI}
        enabled={true}
        isCompleted={true}
        showCustomLabels={true}
        setCustomLabelDialogProps={setCustomLabelDialogProps}
      />
      <SectionDividerLineComponent />
      <Benefits benefits={benefits} selectedData={selectedBenefits} setSelectedData={setSelectedBenefits} />
      <SectionDividerLineComponent />
      {workforces && workforces.length > 0 && (
        <>
          <Workforces
            workforces={workforces}
            selectedData={selectedWorkforces}
            setSelectedData={setSelectedWorkforces}
          />
          <SectionDividerLineComponent />
        </>
      )}
      <TrendAnalysisComponent selectedTrend={selectedTrendAnalysis} setselectedTrend={setSelectedTrendAnalysis} />
      <SectionDividerLineComponent />
      <ExportPermissionComponent
        selectedExportToggle={selectedExportToggle}
        setselectedExportToggle={setSelectedExportToggle}
      />
      <SectionDividerLineComponent />
      <ChatbotComponent selectedChat={selectedChatbot} setSelectedChatbot={setSelectedChatbot} />
      <ChatbotExport
        selectedExport={selectedChatbotExport}
        setSelectedExport={setSelectedChatbotExport}
        disabled={selectedChatbot?.[0] === 0}
      />
      <ChatbotCopy
        selectedCopy={selectedChatbotCopy}
        setSelectedCopy={setSelectedChatbotCopy}
        disabled={selectedChatbot?.[0] === 0}
      />
      <SectionDividerLineComponent />
      <div>
        <SaveButton
          data-testid='save-exit-button'
          onClick={() => handleSave(true)}
          disabled={saveButtonDisableConditions()}
        >
          Save and Exit
        </SaveButton>
        <SaveButton
          data-testid='save-button'
          onClick={() => handleSave(false)}
          disabled={saveButtonDisableConditions()}
        >
          Save
        </SaveButton>
        <CancelButton variant='contrastButton' data-testid='cancel-button' sx={{ml: 1}} onClick={handleCancel}>
          Cancel
        </CancelButton>
        {open && (
          <CancelPopup
            open={open}
            setOpen={setOpen}
            setNavigateToAdminPanel={setNavigateToAdminPanel}
            updateCustomizationWithDefaultData={updateCustomizationWithDefaultData}
          />
        )}
        <SnackbarComponent
          error={updatedLabelError || errorUpdatedPermission}
          showNotification={showNotification}
          setShowNotification={setShowNotification}
          message={
            updatedLabelError || errorUpdatedPermission
              ? 'There is an error while updating the admin dashboard'
              : 'Admin Dashboard updated successfully'
          }
        />
      </div>
      <ConfirmationDialog
        selectedUseCases={affectedUseCases.map((obj) => obj.name)}
        showDialog={showUseCaseConfirmationDialog}
        handleConfirm={handleConfirmOptionDeletion}
        handleClose={() => {
          setShowUseCaseConfirmationDialog(false)
        }}
      />
      <UseCaseNotificationAlert
        message={
          <StyledFlexBox>
            <StyledNotificationInfoOutlinedIcon />
            <StyledNotificationTextBox>
              {affectedUseCases.map((obj) => obj.name).join(', ')}{' '}
              {affectedUseCases.length > 1 ? 'use cases' : 'use case'} automatically been removed!
            </StyledNotificationTextBox>
          </StyledFlexBox>
        }
        vertical='top'
        horizontal='center'
        open={showInfoAlert}
        setOpen={setShowInfoAlert}
      />
      <CustomizationDialog
        setIsLabelCustomized={setIsLabelCustomized}
        data={customLabelDialogProps.data}
        open={customLabelDialogProps.open}
        section={customLabelDialogProps.section}
        initialExpandedRows={customLabelDialogProps.initialExpandedRows}
        handleClose={() => setCustomLabelDialogProps({...customLabelDialogProps, open: false})}
      />
    </MainBoxClient>
  )
}

CustomProjectDetails.propTypes = {
  setIsAdminPanelOpen: PropTypes.func.isRequired,
  resetView: PropTypes.func
}

export default CustomProjectDetails

/* Function Descriptions:
  - useEffect 1 to set the admin panel open state to true on component mount
  - useEffect 2 to reload filters and refresh dashboard when notification is shown and loading is complete
  - useEffect 3 to navigate to admin panel when loading is complete and navigation flag is true
  - useEffect 4 to reset selected filters when data loading is complete and no errors exist
  - useEffect 5 to enable/disable save button based on changes in selected filters or customization
  - useEffect 6 to update custom labels for SDOH when selected SDOH changes
  - useEffect 7 to update custom labels for Health Conditions when selected Health Conditions change
  - useEffect 8 to update custom labels for POI when selected POI changes
  - handleClickOpen opens the cancel confirmation popup
  - handleCancel navigates to admin panel or opens cancel confirmation popup based on save conditions
  - updateCustomLabels updates the custom labels for a specific filter based on selected and current items
  - handleSave saves the updated project settings and optionally exits the customization view
  - saveButtonDisableConditions checks if the save button should be disabled based on various conditions
  - CustomProjectDetails component renders the custom project details view

/**
 * CustomProjectDetails Component
 *
 * This component provides a customizable dashboard view for the admin panel. It allows users to configure
 * various settings such as jurisdictions, data aggregation, health conditions, social determinants of health (SDOH),
 * points of interest (POI), benefits, workforces, trend analysis, export permissions, chatbot settings, and persona flags.
 *
 * The component manages state for selected filters and tracks changes to enable or disable the save button.
 * It also handles saving updated settings, updating custom labels, and navigating back to the admin panel.
 *
 * Key Features:
 * - Displays a customizable dashboard view with multiple configuration options.
 * - Tracks changes in selected filters and enables/disables the save button accordingly.
 * - Supports saving updated settings and updating custom labels for filters.
 * - Provides navigation to the admin panel with confirmation for unsaved changes.
 * - Displays loading indicators and error notifications during data fetching and saving processes.
 *
 * Props:
 * @param {Object} props - Component props.
 * @param {Function} props.setIsAdminPanelOpen - Function to set the admin panel open state.
 *
 * State Management:
 * - Manages state for selected filters (e.g., jurisdiction, health conditions, SDOH, etc.).
 * - Tracks changes in filters to determine if the save button should be enabled.
 * - Handles navigation and confirmation for unsaved changes.
 *
 * Hooks:
 * - useEffect: Handles initialization, state updates, and side effects for various conditions.
 * - useState: Manages local state for selected filters, navigation flags, and UI states.
 *
 * Dependencies:
 * - Uses multiple child components for rendering individual filter sections (e.g., JurisdictionComponent, DataAggregation, etc.).
 * - Relies on context providers (e.g., useAdminPermission, useUpdateCustomSettings) for data and actions.
 *
 * Returns:
 * - Renders the customizable dashboard view with save, cancel, and navigation functionality.
 */
